import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";

import {
  useCompany,
  useInviteUser,
  useUpdateUser,
  type LinkResult,
  type Role,
  type Team,
  type User,
} from "@/api/queries";
import { CopyField } from "@/components/common/CopyField";
import { Field } from "@/components/common/Field";
import { TimezoneSelect } from "@/components/common/TimezoneSelect";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Dialog, DialogClose, DialogContent } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { errorText, fieldError } from "@/lib/errors";
import { formatShortDateTime } from "@/lib/format";
import { cn } from "@/lib/utils";

const roles: Role[] = ["employee", "manager", "admin"];

function RolePicker({
  value,
  onChange,
  disabled,
}: {
  value: Role;
  onChange: (r: Role) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div role="radiogroup" aria-label={t("people.inviteDialog.role")} className="grid gap-2 sm:grid-cols-3">
      {roles.map((r) => (
        <label
          key={r}
          className={cn(
            "flex cursor-pointer flex-col gap-0.5 rounded-lg border p-3 has-checked:border-primary has-checked:bg-primary-soft has-focus-visible:ring-2 has-focus-visible:ring-ring has-disabled:cursor-not-allowed has-disabled:opacity-60",
          )}
        >
          <input
            type="radio"
            name="role"
            value={r}
            checked={value === r}
            disabled={disabled}
            onChange={() => onChange(r)}
            className="sr-only"
          />
          <span className="font-medium">{t(`roles.${r}`)}</span>
          <span className="text-xs text-muted">{t(`people.roleHints.${r}`)}</span>
        </label>
      ))}
    </div>
  );
}

export function InviteDialog({
  teams,
  onClose,
  onInvited,
}: {
  teams: Team[];
  onClose: () => void;
  onInvited: (r: LinkResult) => void;
}) {
  const { t } = useTranslation();
  const company = useCompany();
  const invite = useInviteUser();
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [role, setRole] = useState<Role>("employee");
  const [timezone, setTimezone] = useState<string | null>(null);
  const [teamIds, setTeamIds] = useState<Set<string>>(new Set());
  const tz = timezone ?? company.data?.timezone ?? "Europe/Moscow";

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      const result = await invite.mutateAsync({
        email: email.trim(),
        full_name: fullName.trim(),
        role,
        timezone: tz,
        team_ids: [...teamIds],
      });
      onInvited(result);
    } catch {
      // Shown below.
    }
  };

  const err = invite.error;
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        title={t("people.inviteDialog.title")}
        description={t("people.inviteDialog.description")}
        className="w-[min(92vw,600px)]"
      >
        <form className="flex flex-col gap-5 p-5" onSubmit={(e) => void submit(e)}>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t("people.inviteDialog.fullName")} error={fieldError(t, err, "full_name")}>
              <Input
                required
                autoFocus
                maxLength={120}
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
              />
            </Field>
            <Field label={t("people.inviteDialog.email")} error={fieldError(t, err, "email")}>
              <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
            </Field>
          </div>
          <fieldset className="flex flex-col gap-1.5">
            <legend className="mb-1.5 text-sm font-medium">{t("people.inviteDialog.role")}</legend>
            <RolePicker value={role} onChange={setRole} />
          </fieldset>
          <Field label={t("people.inviteDialog.timezone")}>
            <TimezoneSelect value={tz} onChange={(e) => setTimezone(e.target.value)} />
          </Field>
          {teams.length > 0 && (
            <fieldset className="flex flex-col gap-1.5">
              <legend className="mb-1.5 text-sm font-medium">{t("people.inviteDialog.teams")}</legend>
              <div className="flex flex-wrap gap-2">
                {teams.map((team) => (
                  <label
                    key={team.id}
                    className="flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-1.5 has-checked:border-primary has-checked:bg-primary-soft"
                  >
                    <input
                      type="checkbox"
                      className="size-4 accent-(--primary-solid)"
                      checked={teamIds.has(team.id)}
                      onChange={() =>
                        setTeamIds((prev) => {
                          const next = new Set(prev);
                          if (next.has(team.id)) next.delete(team.id);
                          else next.add(team.id);
                          return next;
                        })
                      }
                    />
                    {team.name}
                  </label>
                ))}
              </div>
            </fieldset>
          )}
          {err && !fieldError(t, err, "email") && !fieldError(t, err, "full_name") && (
            <Alert variant="danger">{errorText(t, err)}</Alert>
          )}
          <div className="flex justify-end gap-2">
            <DialogClose asChild>
              <Button type="button" variant="secondary">
                {t("common.cancel")}
              </Button>
            </DialogClose>
            <Button type="submit" disabled={invite.isPending || !fullName.trim() || !email.includes("@")}>
              {t("people.inviteDialog.submit")}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function LinkDialog({
  mode,
  result,
  onClose,
}: {
  mode: "invite" | "reset";
  result: LinkResult;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage ?? "ru";
  const name = result.user.full_name;
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        title={
          mode === "invite"
            ? t("people.linkDialog.inviteTitle", { name })
            : t("people.linkDialog.resetTitle", { name })
        }
      >
        <div className="flex flex-col gap-4 p-5">
          {result.email_queued ? (
            <Alert variant="success">{t("people.linkDialog.emailSent", { email: result.user.email })}</Alert>
          ) : (
            <Alert variant="warning">{t("people.linkDialog.emailDisabled")}</Alert>
          )}
          <CopyField label={t("people.linkDialog.link")} value={result.url} />
          <p className="text-xs text-muted">
            {t("people.linkDialog.expires", { date: formatShortDateTime(result.expires_at, lang) })}
          </p>
          <div className="flex justify-end">
            <DialogClose asChild>
              <Button>{t("common.close")}</Button>
            </DialogClose>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function EditUserDialog({
  user,
  teams,
  isMe,
  onClose,
}: {
  user: User;
  teams: Team[];
  isMe: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const update = useUpdateUser();
  const [fullName, setFullName] = useState(user.full_name);
  const [role, setRole] = useState<Role>(user.role);
  const [timezone, setTimezone] = useState(user.timezone);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await update.mutateAsync({
        userId: user.id,
        full_name: fullName.trim(),
        timezone,
        ...(isMe ? {} : { role }),
      });
      onClose();
    } catch {
      // Shown below.
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        title={t("people.editDialog.title", { name: user.full_name })}
        description={user.email}
        className="w-[min(92vw,600px)]"
      >
        <form className="flex flex-col gap-5 p-5" onSubmit={(e) => void submit(e)}>
          <Field label={t("people.editDialog.fullName")} error={fieldError(t, update.error, "full_name")}>
            <Input required maxLength={120} value={fullName} onChange={(e) => setFullName(e.target.value)} />
          </Field>
          <fieldset className="flex flex-col gap-1.5">
            <legend className="mb-1.5 text-sm font-medium">{t("people.editDialog.role")}</legend>
            <RolePicker value={role} onChange={setRole} disabled={isMe} />
            {isMe && <p className="text-xs text-muted">{t("errors.cannot_modify_self")}</p>}
          </fieldset>
          <Field label={t("people.editDialog.timezone")}>
            <TimezoneSelect value={timezone} onChange={(e) => setTimezone(e.target.value)} />
          </Field>
          <div className="flex flex-col gap-1">
            <span className="text-sm font-medium">{t("people.editDialog.teams")}</span>
            <p className="text-muted">
              {teams.length ? teams.map((team) => team.name).join(", ") : t("people.noTeams")}
            </p>
            <p className="text-xs text-muted">{t("people.editDialog.teamsHint")}</p>
          </div>
          {update.error && !fieldError(t, update.error, "full_name") && (
            <Alert variant="danger">{errorText(t, update.error)}</Alert>
          )}
          <div className="flex justify-end gap-2">
            <DialogClose asChild>
              <Button type="button" variant="secondary">
                {t("common.cancel")}
              </Button>
            </DialogClose>
            <Button type="submit" disabled={update.isPending || !fullName.trim()}>
              {t("common.save")}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
